import { apim } from "@/constants/api"
import {
  createCartEndPoint,
  getCartEndPoint,
  shippingCostEndPoint,
  discountEndPoint,
  taxEndPoint,
  shareCartEndPoint,
  mergeCartEndPoint,
  cartItemsEndPoint,
  deleteCartEndPoint,
  shipmentFromCartEndPoint,
  addressSuggestionEndPoint,
  addAddressEndPoint,
  paymentEndPoint,
  payPalPaymentEndPoint,
  sharedCartItemsEndPoint,
  ADDRESS_SUGGESTION_DEFAULT_COUNTRY,
  NO_ACTIVE_CART_EXISTS,
} from "@/constants/index"
import {
  CATALOG_PROJECTION_ENDPOINT,
  ORDER_SUBMIT_V2_ENDPOINT,
  ORDER_SUBMIT_V3_ENDPOINT,
} from "@/constants/routes"
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { HYDRATE } from "next-redux-wrapper"
import {
  getQuantityCount,
  getCartIdParams,
  removeCustomerCartId,
} from "@/utils/cart"
import { store } from "@/store"
import { setAnonymousId, clearGuestState } from "@/store/features/authSlice"
import {
  setIsShipmentFromCart,
  setCheckoutAction,
  setExpPayPalCheckout,
  setPayPalCallBackErrorMsg,
  setFromPaymentInventoryCheck,
} from "@/store/features/checkoutSlice"
import { getConfig } from "@/constants/config"
import { getUserPersona } from "@/utils/helper"
import { checkExpPayPal } from "@/utils/payment"
import { redirectToCartPage } from "@/components/checkout/v2/utils"

const initialState = {
  cart: {
    id: "",
  },
  lastUpdatedAt: 0,
  lastUpdatedCartId: "",
  lastUpdateSource: "",
  quantity: 0,
  shipping: {},
  shipment: {},
  cartCount: 0,
  // shpping Items to checkout when product stock available
  shippingItemstoCheckout: [],
  // no items to ship from checkout when product is out of stock
  noShippingItemsInCheckout: [],
  cartUrl: "",
  showShippingDetailsForReview: {
    Parcel: false,
    LTL: false,
    Services: false,
  },
  paymentId: "",
  nameOnCard: "",
  status: "",
  isCartLoading: false,
  latestGetCartRequestId: "",
  latestGetCartMutationVersion: 0,
  latestGetCartBlockedByMutation: false,
  cartMutationVersion: 0,
  activeCartMutationCount: 0,
  pendingCartMutationRequestIds: {},
  invalidatedCartMutationRequestIds: {},
  cartAuthContextVersion: 0,
  latestCartLoadGeneration: "",
  // shipping Restricted
  isShippingRestricted: false,
  shippingRestrictionMessage: "",
  shippingRestrictedAddress: "",
  shippingAddressFromActiveCart: {},
}

const AUTH_CONTEXT_ACTION_TYPES = new Set([
  "auth/postLogin/fulfilled",
  "auth/clearAuthState",
  "auth/logout/fulfilled",
])

const isAuthContextAction = action => AUTH_CONTEXT_ACTION_TYPES.has(action.type)

const applyCartResponse = (state, cart, source) => {
  state.cart = cart
  state.cartCount = getQuantityCount(cart?.lineItems) ?? 0
  state.lastUpdatedAt = Date.now()
  state.lastUpdatedCartId = cart?.id ?? ""
  state.lastUpdateSource = source
}

const isCurrentCartLoadAction = (state, action) => {
  const generation = action?.meta?.arg?.cartLoadGeneration
  return Boolean(
    !generation ||
      !state.latestCartLoadGeneration ||
      generation === state.latestCartLoadGeneration
  )
}

const isCurrentGetCartRequest = (
  state = {},
  requestId = "",
  cartLoadGeneration = ""
) => {
  const latestGeneration = state.latestCartLoadGeneration || ""
  const latestRequestId = state.latestGetCartRequestId || ""
  const latestMutationVersion = state.latestGetCartMutationVersion ?? 0
  const currentMutationVersion = state.cartMutationVersion ?? 0

  return Boolean(
    (!cartLoadGeneration ||
      !latestGeneration ||
      cartLoadGeneration === latestGeneration) &&
      (!latestRequestId || latestRequestId === requestId) &&
      !state.latestGetCartBlockedByMutation &&
      latestMutationVersion === currentMutationVersion
  )
}

const isCurrentGetCartAction = (state, action) =>
  isCurrentCartLoadAction(state, action) &&
  isCurrentGetCartRequest(
    state,
    action?.meta?.requestId,
    action?.meta?.arg?.cartLoadGeneration
  )

const isCurrentCartMutationRequest = (
  state = {},
  requestId = "",
  cartLoadGeneration = ""
) => {
  const pendingVersion = state.pendingCartMutationRequestIds?.[requestId]
  const currentAuthContextVersion = state.cartAuthContextVersion ?? 0
  const latestGeneration = state.latestCartLoadGeneration || ""

  return Boolean(
    requestId &&
      pendingVersion !== undefined &&
      pendingVersion === currentAuthContextVersion &&
      (!cartLoadGeneration ||
        !latestGeneration ||
        cartLoadGeneration === latestGeneration)
  )
}

const isCurrentCartMutationAction = (state, action) =>
  isCurrentCartLoadAction(state, action) &&
  isCurrentCartMutationRequest(
    state,
    action?.meta?.requestId,
    action?.meta?.arg?.cartLoadGeneration
  )

const invalidateCartResponseState = state => {
  if (!state.invalidatedCartMutationRequestIds) {
    state.invalidatedCartMutationRequestIds = {}
  }
  Object.keys(state.pendingCartMutationRequestIds || {}).forEach(requestId => {
    state.invalidatedCartMutationRequestIds[requestId] = true
  })
  state.pendingCartMutationRequestIds = {}
  state.activeCartMutationCount = 0
  state.cartMutationVersion = (state.cartMutationVersion ?? 0) + 1
  state.cartAuthContextVersion = (state.cartAuthContextVersion ?? 0) + 1
  state.latestGetCartBlockedByMutation = false
  state.lastUpdatedAt = 0
  state.lastUpdatedCartId = ""
  state.lastUpdateSource = ""

  if (state.status === "loading") state.status = ""
}

const markCartMutationPending = (state, action) => {
  if (!isCurrentCartLoadAction(state, action)) return

  const requestId = action?.meta?.requestId
  if (!requestId) return

  if (!state.pendingCartMutationRequestIds) {
    state.pendingCartMutationRequestIds = {}
  }
  if (state.pendingCartMutationRequestIds[requestId] !== undefined) {
    return
  }

  state.pendingCartMutationRequestIds[requestId] =
    state.cartAuthContextVersion ?? 0
  state.cartMutationVersion = (state.cartMutationVersion ?? 0) + 1
  state.activeCartMutationCount = (state.activeCartMutationCount ?? 0) + 1
}

const settleCartMutation = (state, action) => {
  const requestId = action?.meta?.requestId
  if (
    !requestId ||
    state.pendingCartMutationRequestIds?.[requestId] === undefined
  ) {
    return
  }

  delete state.pendingCartMutationRequestIds[requestId]
  state.activeCartMutationCount = Math.max(
    (state.activeCartMutationCount ?? 1) - 1,
    0
  )
}

export const getCart = createAsyncThunk(
  "getCart",
  async (payload = {}, thunkAPI) => {
    const { isNotFromCart = "", klId = "", cartLoadGeneration = "" } = payload
    const { cartId = "" } = getCartIdParams()
    const url = klId
      ? `/cart/${klId}` // it's for loading customer cart data using KL id
      : `${getCartEndPoint}${
          isNotFromCart ? "&gfqprofile=refershCartProfile" : ""
        }${cartId}`

    const { data = {} } = await apim.get(url)
    const { anonymousId = "" } = data
    const cartState = thunkAPI.getState()?.cart || {}
    if (
      isCurrentGetCartRequest(cartState, thunkAPI.requestId, cartLoadGeneration)
    ) {
      thunkAPI.dispatch(setAnonymousId(anonymousId))
    }
    return data
  }
)

export const createCart = createAsyncThunk(
  "createCart",
  async (body, thunkAPI) => {
    const { cartLoadGeneration = "", ...cartBody } = body || {}
    const state = thunkAPI.getState() || {}
    const requestAuthContextVersion = state.cart?.cartAuthContextVersion ?? 0
    const { user } = state.auth || {}
    if (user) cartBody.customerEmail = user.sub?.toLowerCase() ?? ""

    if (user.koc) {
      cartBody.custom = {
        type: { key: "order", typeId: "type" },
        fields: { CSRAssociateId: user.koc, orderType: "ISC" },
      }
    }

    const { data = {} } = await apim.post(`${createCartEndPoint}`, cartBody)
    const { anonymousId = "" } = data
    const cartState = thunkAPI.getState()?.cart || {}
    if (
      requestAuthContextVersion === cartState.cartAuthContextVersion &&
      isCurrentCartMutationRequest(
        cartState,
        thunkAPI.requestId,
        cartLoadGeneration
      )
    ) {
      thunkAPI.dispatch(setAnonymousId(anonymousId))
    }
    return data
  }
)

export const shippingCost = createAsyncThunk("shippingCost", async id => {
  const response = await apim.get(`${shippingCostEndPoint}?cartId=${id}`)
  return response.data
})

export const discount = createAsyncThunk("discount", async body => {
  const { cartId = "" } = getCartIdParams()
  const url = `${discountEndPoint}${cartId}`
  const response = await apim.post(url, body)
  return response.data
})

export const taxCalculation = createAsyncThunk("tax", async () => {
  const { cartId = "" } = getCartIdParams()
  const body = { actions: [{ action: "calculateTax" }] }
  const url = `${taxEndPoint}${cartId}`
  const response = await apim.post(url, body)
  return response.data
})

export const shipmentUpdate = createAsyncThunk(
  "shipmentUpdate",
  async (body, thunkAPI) => {
    try {
      const { cartId = "" } = getCartIdParams()
      const url = `${taxEndPoint}${cartId}`
      const response = await apim.post(url, body)
      return response.data
    } catch (error) {
      const {
        response: { data: { isNonDeliverableAddress = false } = {} } = {},
      } = error
      return thunkAPI.rejectWithValue({ isNonDeliverableAddress })
    }
  }
)

export const shareCart = createAsyncThunk("sharecart", async body => {
  const response = await apim.post(`${shareCartEndPoint}`, body)
  return response.data
})

export const mergeCart = createAsyncThunk(
  "cart/merge",
  async (payload, thunkAPI) => {
    const requestAuthContextVersion =
      thunkAPI.getState()?.cart?.cartAuthContextVersion ?? 0
    const { data = {} } = await apim.post(mergeCartEndPoint, payload)
    const cartState = thunkAPI.getState()?.cart || {}
    if (
      data &&
      data.type === "Cart" &&
      requestAuthContextVersion === cartState.cartAuthContextVersion &&
      isCurrentCartMutationRequest(cartState, thunkAPI.requestId)
    ) {
      store.dispatch(setAnonymousId(""))
    }
    return data
  }
)
export const addToCart = createAsyncThunk(
  "addToCart",
  async (body, thunkAPI) => {
    const { cartLoadGeneration = "", ...cartBody } = body || {}
    const persona = getUserPersona()
    const state = thunkAPI.getState() || {}
    const requestAuthContextVersion = state.cart?.cartAuthContextVersion ?? 0

    const {
      cart: { customerGroup },
    } = state.cart

    if (!customerGroup) {
      const customerGroupData = {}
      customerGroupData["action"] = "setCustomerGroup"
      customerGroupData["customerGroup"] = { key: persona }
      if (cartBody.actions) {
        cartBody.actions.push(customerGroupData)
      }
    }
    const { cartId = "" } = getCartIdParams()
    const url = `${cartItemsEndPoint}?expand=calculation${cartId}`
    const { data = {} } = await apim.post(url, cartBody)
    const { anonymousId = "" } = data
    const cartState = thunkAPI.getState()?.cart || {}
    if (
      requestAuthContextVersion === cartState.cartAuthContextVersion &&
      isCurrentCartMutationRequest(
        cartState,
        thunkAPI.requestId,
        cartLoadGeneration
      )
    ) {
      thunkAPI.dispatch(setAnonymousId(anonymousId))
      const kohlerDatalayer = window.kohler.datalayer
      if (
        typeof kohlerDatalayer !== "undefined" &&
        typeof kohlerDatalayer.setItem === "function"
      ) {
        kohlerDatalayer.setItem("CART_DATA", data)
      }
    }
    return data
  }
)

export const shareCartaddToCart = createAsyncThunk(
  "shareCartaddToCart",
  async (body, thunkAPI) => {
    const requestAuthContextVersion =
      thunkAPI.getState()?.cart?.cartAuthContextVersion ?? 0
    const { cartId = "", isSharedCart = false, ...restBody } = body
    const url = `${sharedCartItemsEndPoint}?expand=calculation&cartId=${cartId}`
    const { data = {} } = await apim.post(url, restBody)
    const state = thunkAPI.getState() || {}
    const cartState = state.cart || {}
    if (
      data.anonymousId &&
      !state.auth?.isAuth &&
      requestAuthContextVersion === cartState.cartAuthContextVersion &&
      isCurrentCartMutationRequest(cartState, thunkAPI.requestId)
    ) {
      thunkAPI.dispatch(setAnonymousId(data.anonymousId))
    }
    return { data, isSharedCart }
  }
)

export const removeFromCartLineItem = createAsyncThunk(
  "removeFromCartLineItem",
  async (body, thunkAPI) => {
    const requestAuthContextVersion =
      thunkAPI.getState()?.cart?.cartAuthContextVersion ?? 0
    const { cartId = "" } = getCartIdParams()
    const url = `${cartItemsEndPoint}?expand=calculation${cartId}`
    const response = await apim.post(url, body)
    const cartState = thunkAPI.getState()?.cart || {}
    if (
      requestAuthContextVersion === cartState.cartAuthContextVersion &&
      isCurrentCartMutationRequest(cartState, thunkAPI.requestId)
    ) {
      const kohlerDatalayer = window.kohler.datalayer
      if (
        typeof kohlerDatalayer !== "undefined" &&
        typeof kohlerDatalayer.setItem === "function"
      ) {
        kohlerDatalayer.setItem("CART_DATA", response.data)
      }
    }
    return response.data
  }
)
export const deleteCart = createAsyncThunk("deleteCart", async () => {
  const response = await apim.delete(`${deleteCartEndPoint}`)

  return response.data
})
export const shareCartDelete = createAsyncThunk(
  "shareCartDelete",
  async body => {
    const { id, version } = body
    const response = await apim.delete(
      `${`/cart/${id}?cart-versionId=${version}`}`
    )
    return response.data
  }
)

export const shipmentCart = createAsyncThunk("shipmentcart", async body => {
  let response
  const { cartId = "", cartIdQuery = "" } = getCartIdParams()
  const {
    cart: { cart = {} },
    checkout: {
      checkoutStep = 0,
      isFromPaymentInventoryCheck = false,
      isVerifiedAddress = "",
      useThisVerifiedAddress = "",
    },
  } = store.getState() || {}

  store.dispatch(setIsShipmentLoading(true))

  const isXpp = checkExpPayPal()

  if (checkoutStep < 2 && isFromPaymentInventoryCheck) {
    store.dispatch(setFromPaymentInventoryCheck(false))
  }

  try {
    if (body && body?.assign && body?.payload) {
      const url = `${shipmentFromCartEndPoint}${
        body.assign ? "?assignshipping=true" : ""
      }${cartId}`
      response = await apim.post(url, body.payload)
    } else {
      const url = `${shipmentFromCartEndPoint}${cartIdQuery}`
      response = await apim.post(url)
    }
    store.dispatch(setIsShipmentLoading(false))
    store.dispatch(setIsShipmentFromCart(true))

    if (isVerifiedAddress) {
      if (isXpp) {
        store.dispatch(setCheckoutAction(4))
        store.dispatch(setExpPayPalCheckout(true))
      } else if (checkoutStep >= 2 && isFromPaymentInventoryCheck) {
        store.dispatch(setCheckoutAction(2))
      } else {
        store.dispatch(setCheckoutAction(2))
      }
    } else if (useThisVerifiedAddress && !isVerifiedAddress) {
      if (isXpp) {
        store.dispatch(setCheckoutAction(5))
      } else {
        store.dispatch(setCheckoutAction(2))
      }
    } else {
      store.dispatch(setCheckoutAction(1))
      if (isXpp) {
        store.dispatch(setExpPayPalCheckout(true))
      }
    }

    return response.data
  } catch (error) {
    const { response: { data: { error: errorMessage = "" }, status } = {} } =
      error

    if (errorMessage === NO_ACTIVE_CART_EXISTS) {
      redirectToCartPage()
    }

    const shippingDetails = {
      "shipping-details": { results: [] },
      shippingCost: true,
    }
    response = await apim.get(`${shippingCostEndPoint}?cartId=${cart?.id}`)
    shippingDetails["shipping-details"]["results"] = response?.data?.results
    store.dispatch(setIsShipmentLoading(false))
    store.dispatch(setIsShipmentFromCart(false))
    if (status === 401 || status === 403) {
      store.dispatch(setIsShipmentFromCart(true))
      store.dispatch(setPayPalCallBackErrorMsg(body))
    }
    return shippingDetails
  }
})

export const addresssSuggest = createAsyncThunk(
  "addresssSuggest",
  async data => {
    const { addr = "", geoLocationData = {} } = data || {}
    const config = await getConfig()
    const { apiEndpoints: { addressSuggestionsLimit = "" } = {} } = config || {}

    const country =
      config?.apiEndPointData?.map?.experianISOCode ||
      ADDRESS_SUGGESTION_DEFAULT_COUNTRY
    let path = `${addressSuggestionEndPoint}?query=${encodeURIComponent(
      addr
    )}&country=${country}${
      addressSuggestionsLimit ? `&limit=${addressSuggestionsLimit}` : ""
    }`
    if (geoLocationData?.lat && geoLocationData?.lng) {
      path += `&location=${geoLocationData.lat},${geoLocationData.lng}`
    }
    const response = await apim.get(path)
    return response.data
  }
)

export const addAddress = createAsyncThunk("addAddress", async body => {
  const response = await apim.post(`${addAddressEndPoint}`, body)
  return response.data
})

export const payment = createAsyncThunk("payment", async body => {
  const response = await apim.post(`${paymentEndPoint}`, body)
  return response.data
})

export const payPalPayment = createAsyncThunk("payPalPayment", async body => {
  const response = await apim.post(`${payPalPaymentEndPoint}`, body)
  return response.data
})

export const orderSubmit = createAsyncThunk(
  "orderSubmit",
  async (body, thunkAPI) => {
    const { cartIdQuery = "", activeCartId } = getCartIdParams(true)
    const {
      timeout,
      isAciCreditCardV2,
      isAciElavonFlow = false,
      ...restBody
    } = body || {}
    const url = isAciCreditCardV2
      ? ORDER_SUBMIT_V3_ENDPOINT
      : `${ORDER_SUBMIT_V2_ENDPOINT}${cartIdQuery}`

    const payload = {
      ...restBody,
      ...(isAciCreditCardV2 && { cartId: activeCartId }),
      ...(isAciElavonFlow && { paymentTarget: "EPG" }),
    }

    const isOrderError = sessionStorage.getItem("isOrderError")

    const requestConfig = {
      ...(timeout && { timeout: timeout }),
      ...(isAciCreditCardV2 &&
        isOrderError === "true" && { headers: { "X-Iframe-Retry": "true" } }),
    }

    if (activeCartId) {
      try {
        const { data, status } = await apim.post(url, payload, requestConfig)
        const state = thunkAPI.getState()
        const { auth: { isAuth = false } = {} } = state

        if (status === 201 || status === 200) {
          sessionStorage.removeItem("isOrderError")
          removeCustomerCartId()
          if (!isAuth) {
            store.dispatch(clearGuestState())
          }
        }
        return data
      } catch (error) {
        if (isAciCreditCardV2) {
          sessionStorage.setItem("isOrderError", "true")
        }
        const { response: { data = {} } = {} } = error
        console.error(error)
        return thunkAPI.rejectWithValue(data)
      }
    }
  }
)

export const recentlyViewed = createAsyncThunk(
  "recentlyViewed",
  async query => {
    const response = await apim.get(
      `${CATALOG_PROJECTION_ENDPOINT}?where=id in (${query})`
    )
    return response.data
  }
)

const CART_MUTATION_PENDING_TYPES = new Set([
  createCart.pending.type,
  discount.pending.type,
  taxCalculation.pending.type,
  shipmentUpdate.pending.type,
  addToCart.pending.type,
  deleteCart.pending.type,
  shipmentCart.pending.type,
  mergeCart.pending.type,
  removeFromCartLineItem.pending.type,
  shareCartaddToCart.pending.type,
  shareCartDelete.pending.type,
  orderSubmit.pending.type,
])

const CART_MUTATION_SETTLED_TYPES = new Set([
  createCart.fulfilled.type,
  createCart.rejected.type,
  discount.fulfilled.type,
  discount.rejected.type,
  taxCalculation.fulfilled.type,
  taxCalculation.rejected.type,
  shipmentUpdate.fulfilled.type,
  shipmentUpdate.rejected.type,
  addToCart.fulfilled.type,
  addToCart.rejected.type,
  deleteCart.fulfilled.type,
  deleteCart.rejected.type,
  shipmentCart.fulfilled.type,
  shipmentCart.rejected.type,
  mergeCart.fulfilled.type,
  mergeCart.rejected.type,
  removeFromCartLineItem.fulfilled.type,
  removeFromCartLineItem.rejected.type,
  shareCartaddToCart.fulfilled.type,
  shareCartaddToCart.rejected.type,
  shareCartDelete.fulfilled.type,
  shareCartDelete.rejected.type,
  orderSubmit.fulfilled.type,
  orderSubmit.rejected.type,
])

const isCartMutationPendingAction = action =>
  CART_MUTATION_PENDING_TYPES.has(action.type)

const isCartMutationSettledAction = action =>
  CART_MUTATION_SETTLED_TYPES.has(action.type)

export const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    setCart(state, action) {
      state.cartMutationVersion = (state.cartMutationVersion ?? 0) + 1
      applyCartResponse(state, action.payload, "setCart")
      // Resolve the getCart this bump orphaned, unless a mutation still owns status.
      if ((state.activeCartMutationCount ?? 0) === 0) {
        state.status = "succeeded"
      }
    },
    setCartCount(state, action) {
      state.cartCount = action.payload
    },
    setQtyLoading(state, action) {
      state.qtyLoading = action.payload
    },
    setShipping(state, action) {
      state.shippingItemstoCheckout = action.payload
    },
    setNoshipping(state, action) {
      const skuIds = action?.payload?.map(item => item.sku)
      state.noShippingItemsInCheckout = skuIds || []
    },
    setShowShippingDetails(state, action) {
      state.showShippingDetailsForReview = action.payload
    },
    setCartUrl(state, action) {
      state.cartUrl = action.payload
    },
    setPaymetId(state, action) {
      state.paymentId = action.payload
    },
    increment(state, action) {
      state.quantity = state.quantity + 1
    },
    decrement(state, action) {
      state.quantity = state.quantity - 1
    },
    setNameOnCard(state, action) {
      state.nameOnCard = action.payload
    },
    setCardStatus(state, action) {
      state.isCartLoading = action.payload
    },
    setCartLoadGeneration(state, action) {
      state.latestCartLoadGeneration = action.payload || ""
    },
    invalidateCartResponses(state) {
      invalidateCartResponseState(state)
    },
    setIsPayPalCallback(state, action) {
      state.isPayPalCallback = action.payload
    },
    setIsShipmentLoading(state, action) {
      state.isShipmentLoading = action.payload
    },
    setIsShipmentRestricted(state, action) {
      if (typeof action.payload === "object" && action.payload !== null) {
        const {
          isRestricted = false,
          warningMessage = "",
          restrictedUserAddress,
        } = action.payload
        state.isShippingRestricted = isRestricted
        state.shippingRestrictionMessage = warningMessage
        state.shippingRestrictedAddress = restrictedUserAddress || ""
        return
      }

      state.isShippingRestricted = action.payload
      state.shippingRestrictionMessage = action.payload
        ? state.shippingRestrictionMessage
        : ""
      state.shippingRestrictedAddress = action.payload
        ? state.shippingRestrictedAddress
        : ""
    },
    setShippingAddress(state, action) {
      state.shippingAddressFromActiveCart = action.payload || {}
    },
  },
  extraReducers(builder) {
    builder
      .addCase([HYDRATE], (state, action) => {
        const hydratedCart = action.payload.cart || {}
        return {
          ...state,
          ...hydratedCart,
          latestGetCartRequestId:
            state.latestGetCartRequestId ??
            hydratedCart.latestGetCartRequestId ??
            "",
          latestGetCartMutationVersion:
            state.latestGetCartMutationVersion ??
            hydratedCart.latestGetCartMutationVersion ??
            0,
          latestGetCartBlockedByMutation:
            state.latestGetCartBlockedByMutation ??
            hydratedCart.latestGetCartBlockedByMutation ??
            false,
          cartMutationVersion:
            state.cartMutationVersion ?? hydratedCart.cartMutationVersion ?? 0,
          activeCartMutationCount:
            state.activeCartMutationCount ??
            hydratedCart.activeCartMutationCount ??
            0,
          pendingCartMutationRequestIds:
            state.pendingCartMutationRequestIds ??
            hydratedCart.pendingCartMutationRequestIds ??
            {},
          invalidatedCartMutationRequestIds:
            state.invalidatedCartMutationRequestIds ??
            hydratedCart.invalidatedCartMutationRequestIds ??
            {},
          cartAuthContextVersion:
            state.cartAuthContextVersion ??
            hydratedCart.cartAuthContextVersion ??
            0,
          latestCartLoadGeneration:
            state.latestCartLoadGeneration ??
            hydratedCart.latestCartLoadGeneration ??
            "",
        }
      })
      .addCase(getCart.pending, (state, action) => {
        if (!isCurrentCartLoadAction(state, action)) return
        state.status = "loading"
        state.latestGetCartRequestId = action.meta.requestId
        state.latestGetCartMutationVersion = state.cartMutationVersion ?? 0
        state.latestGetCartBlockedByMutation =
          (state.activeCartMutationCount ?? 0) > 0
      })
      .addCase(getCart.fulfilled, (state, action) => {
        if (!isCurrentGetCartAction(state, action)) return
        applyCartResponse(state, action.payload, "getCart")
        state.status = "succeeded"
      })
      .addCase(getCart.rejected, (state, action) => {
        if (!isCurrentGetCartAction(state, action)) return
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(createCart.pending, (state, action) => {
        if (!isCurrentCartLoadAction(state, action)) return
        state.status = "loading"
      })
      .addCase(createCart.fulfilled, (state, action) => {
        if (!isCurrentCartMutationAction(state, action)) return
        applyCartResponse(state, action.payload, "createCart")
        state.status = "succeeded"
      })
      .addCase(createCart.rejected, (state, action) => {
        if (!isCurrentCartMutationAction(state, action)) return
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(shippingCost.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(shippingCost.fulfilled, (state, action) => {
        state.shipping = action.payload
        state.status = "succeeded"
      })
      .addCase(shippingCost.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })

      .addCase(discount.pending, state => {
        state.status = "loading"
      })
      .addCase(discount.fulfilled, (state, action) => {
        if (!isCurrentCartMutationAction(state, action)) return
        state.status = "succeeded"
        applyCartResponse(state, action.payload, "discount")
      })
      .addCase(discount.rejected, (state, action) => {
        if (!isCurrentCartMutationAction(state, action)) return
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(taxCalculation.pending, state => {
        state.status = "loading"
      })
      .addCase(taxCalculation.fulfilled, (state, action) => {
        if (!isCurrentCartMutationAction(state, action)) return
        state.status = "succeeded"
        applyCartResponse(state, action.payload, "taxCalculation")
      })
      .addCase(taxCalculation.rejected, (state, action) => {
        if (!isCurrentCartMutationAction(state, action)) return
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(shipmentUpdate.pending, state => {
        state.status = "loading"
      })
      .addCase(shipmentUpdate.fulfilled, (state, action) => {
        if (!isCurrentCartMutationAction(state, action)) return
        state.status = "succeeded"
        if (action.payload?.type === "Cart") {
          applyCartResponse(state, action.payload, "shipmentUpdate")
        }
      })
      .addCase(shipmentUpdate.rejected, (state, action) => {
        if (!isCurrentCartMutationAction(state, action)) return
        state.status = "failed"
        state.error = action.error.message
        state.isNonDeliverableAddress = action.payload?.isNonDeliverableAddress
      })
      .addCase(shareCart.pending, state => {
        // state.status = "loading"
      })
      .addCase(shareCart.fulfilled, (state, action) => {
        // state.status = "succeeded"
      })
      .addCase(shareCart.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(addToCart.pending, (state, action) => {
        if (!isCurrentCartLoadAction(state, action)) return
        // state.status = "loading"
        state.status = "loading"
      })
      .addCase(addToCart.fulfilled, (state, action) => {
        if (!isCurrentCartMutationAction(state, action)) return
        applyCartResponse(state, action.payload, "addToCart")
        state.status = "succeeded"
      })
      .addCase(addToCart.rejected, (state, action) => {
        if (!isCurrentCartMutationAction(state, action)) return
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(deleteCart.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(deleteCart.fulfilled, (state, action) => {
        if (!isCurrentCartMutationAction(state, action)) return
        // state.cart = { id: action.payload.id, lineItems: [] }
        state.cartCount = 0
        state.status = "succeeded"
      })
      .addCase(deleteCart.rejected, (state, action) => {
        if (!isCurrentCartMutationAction(state, action)) return
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(shipmentCart.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(shipmentCart.fulfilled, (state, action) => {
        if (!isCurrentCartMutationAction(state, action)) return
        if (!action.payload?.shippingCost) {
          applyCartResponse(
            state,
            { ...state.cart, ...action.payload },
            "shipmentCart"
          )
        }
        state.status = "succeeded"
      })
      .addCase(shipmentCart.rejected, (state, action) => {
        if (!isCurrentCartMutationAction(state, action)) return
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(addresssSuggest.pending, (state, action) => {
        // state.status = "loading"
      })
      .addCase(addresssSuggest.fulfilled, (state, action) => {
        state.status = "succeeded"
      })
      .addCase(addresssSuggest.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(addAddress.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(addAddress.fulfilled, (state, action) => {
        // state.cart = action.payload
        state.status = "succeeded"
      })
      .addCase(addAddress.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(mergeCart.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(mergeCart.fulfilled, (state, action) => {
        if (!isCurrentCartMutationAction(state, action)) return
        applyCartResponse(state, action.payload, "mergeCart")
        state.status = "succeeded"
      })
      .addCase(mergeCart.rejected, (state, action) => {
        if (!isCurrentCartMutationAction(state, action)) return
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(payment.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(payment.fulfilled, (state, action) => {
        state.status = "succeeded"
      })
      .addCase(payment.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(payPalPayment.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(payPalPayment.fulfilled, (state, action) => {
        state.status = "succeeded"
      })
      .addCase(payPalPayment.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(orderSubmit.pending, (state, action) => {
        state.status = "loading" || ""
      })
      .addCase(orderSubmit.fulfilled, (state, action) => {
        if (!isCurrentCartMutationAction(state, action)) return
        // state.cart = action.payload
        state.status = "succeeded" || ""
      })
      .addCase(orderSubmit.rejected, (state, action) => {
        if (!isCurrentCartMutationAction(state, action)) return
        state.status = "failed"
        state.error = action.error.message || ""
      })
      .addCase(removeFromCartLineItem.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(removeFromCartLineItem.fulfilled, (state, action) => {
        if (!isCurrentCartMutationAction(state, action)) return
        applyCartResponse(state, action.payload, "removeFromCartLineItem")
        state.status = "succeeded"
      })
      .addCase(removeFromCartLineItem.rejected, (state, action) => {
        if (!isCurrentCartMutationAction(state, action)) return
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(recentlyViewed.pending, state => {
        state.status = "loading"
      })
      .addCase(recentlyViewed.fulfilled, (state, action) => {
        state.status = "succeeded"
      })
      .addCase(recentlyViewed.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(shareCartaddToCart.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(shareCartaddToCart.fulfilled, (state, action) => {
        if (!isCurrentCartMutationAction(state, action)) return
        const { data } = action.payload
        applyCartResponse(state, data, "shareCartaddToCart")
        state.status = "succeeded"
      })
      .addCase(shareCartaddToCart.rejected, (state, action) => {
        if (!isCurrentCartMutationAction(state, action)) return
        state.status = "failed"
        state.error = action.error.message
      })
      .addCase(shareCartDelete.pending, (state, action) => {
        state.status = "loading"
      })
      .addCase(shareCartDelete.fulfilled, (state, action) => {
        if (!isCurrentCartMutationAction(state, action)) return
        state.cart = { id: action.payload.id, lineItems: [] }
        state.cartCount = 0
        state.shippingItemstoCheckout = []
        state.lastUpdatedAt = Date.now()
        state.lastUpdatedCartId = action.payload.id || ""
        state.lastUpdateSource = "shareCartDelete"
        state.status = "succeeded"
      })
      .addCase(shareCartDelete.rejected, (state, action) => {
        if (!isCurrentCartMutationAction(state, action)) return
        state.status = "failed"
        state.error = action.error.message
      })
      .addMatcher(isCartMutationPendingAction, (state, action) => {
        markCartMutationPending(state, action)
      })
      .addMatcher(isCartMutationSettledAction, (state, action) => {
        settleCartMutation(state, action)
      })
      .addMatcher(isAuthContextAction, state => {
        invalidateCartResponseState(state)
      })
  },
})

export const {
  setCart,
  increment,
  decrement,
  setCartCount,
  setQtyLoading,
  setCartLoadGeneration,
  invalidateCartResponses,
  setCartUrl,
  setShipping,
  setNoshipping,
  setShowShippingDetails,
  setPaymetId,
  setNameOnCard,
  setCardStatus,
  setIsPayPalCallback,
  setIsShipmentLoading,
  setIsShipmentRestricted,
  setShippingAddress,
} = cartSlice.actions
export const selectCartState = state => state.cart
export default cartSlice.reducer
